import { ComponentProps } from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useReducedMotion } from "@/shared/hooks/use-reduced-motion"

/**
 * Card Design System
 * Unified card component with variants: default, elevated, outlined, interactive
 */

const cardVariants = cva(
  "flex flex-col rounded-xl transition-all duration-200",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground border shadow-sm",
        elevated: "bg-card text-card-foreground shadow-lg border-0",
        outlined: "bg-transparent text-card-foreground border-2 border-border shadow-none",
        interactive: [
          "bg-card text-card-foreground border shadow-sm cursor-pointer",
          "hover:shadow-lg hover:-translate-y-1 hover:border-primary/50",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          "active:translate-y-0 active:shadow-md"
        ].join(" "),
        ghost: "bg-transparent border-0 shadow-none",
      },
      size: {
        sm: "gap-3 py-3",
        md: "gap-4 py-4",
        lg: "gap-6 py-6",
      },
      state: {
        default: "",
        disabled: "opacity-50 pointer-events-none",
        loading: "pointer-events-none",
      }
    },
    defaultVariants: {
      variant: "default",
      size: "lg",
      state: "default",
    },
  }
)

interface CardProps extends ComponentProps<"div">, VariantProps<typeof cardVariants> {
  loading?: boolean
}

function Card({ className, variant, size, state, loading, ...props }: CardProps) {
  const prefersReducedMotion = useReducedMotion()

  return (
    <div
      data-slot="card"
      className={cn(
        cardVariants({ 
          variant, 
          size, 
          state: loading ? 'loading' : state 
        }),
        prefersReducedMotion && "transition-none",
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn("leading-none font-semibold", className)}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("px-6", className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      {...props}
    />
  )
}

function CardMedia({ 
  className, 
  src, 
  alt = "",
  aspectRatio = "video",
  ...props 
}: ComponentProps<"div"> & { 
  src?: string
  alt?: string
  aspectRatio?: "video" | "square" | "wide"
}) {
  const aspectClasses = {
    video: "aspect-video",
    square: "aspect-square",
    wide: "aspect-[21/9]",
  }
  
  return (
    <div
      data-slot="card-media"
      className={cn(
        "overflow-hidden rounded-t-xl -mt-6 -mx-0 first:mt-0",
        aspectClasses[aspectRatio],
        className
      )}
      {...props}
    >
      {src && (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      )}
    </div>
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  CardMedia,
  cardVariants,
}
